import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { filter } from 'lodash';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import plusFill from '@iconify/icons-eva/plus-fill';
import { LinkasRouterLink, useNavigate } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  TextField
} from '@mui/material';
// utils
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment-timezone';
//
import * as tokenServices from '../services/token';
// components
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { TokenListHead, TokenListToolbar, TokenMoreMenu } from '../components/_dashboard/token';
import USERLIST from '../_mocks_/user';
import isEligible from '../utils/roleEligibleGetter';
import AccessRequired from './AccessRequired';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'id', label: 'ID', alignRight: false },
  { id: 'description', label: 'Deskripsi', alignRight: false },
  { id: 'createdBy', label: 'Created By', alignRight: false },
  { id: 'usedBy', label: 'Used By', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'createdAt', label: 'Created At', alignRight: false },
  { id: '' }
];

//

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) =>
        _user?.id?.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _user?.description?.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

Token.propTypes = {
  user: PropTypes.object.isRequired
};

export default function Token({ user }) {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tokens, setTokens] = React.useState([]);
  const [toggleRefresh, setToggleRefresh] = React.useState(false);
  const [description, setDescription] = React.useState('');
  const navigate = useNavigate();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = tokens.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const deleteData = async (rowData) => {
    confirmAlert({
      title: 'Konfirmasi',
      message: 'Apakah Anda yakin akan menghapus data ini?',
      buttons: [
        {
          label: 'Yakin',
          onClick: async () => {
            try {
              const body = { id: rowData.id };
              if (!body.id) return;
              toast
                .promise(tokenServices.deleteByID(body.id), {
                  loading: 'Menghapus data ...',
                  error: <b>Maaf, terjadi kesalahan</b>,
                  success: <b>Data berhasil dihapus</b>
                })
                .then(() => {
                  setToggleRefresh(!toggleRefresh);
                });
            } catch (error) {
              toast.error(error.message);
            }
          }
        },
        {
          label: 'Tidak',
          onClick: () => console.log('No')
        }
      ]
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tokens.length) : 0;

  const filteredData = applySortFilter(tokens, getComparator(order, orderBy), filterName);

  const isUserNotFound = filteredData.length === 0;

  const getTokens = async () => {
    try {
      const userRole = user?.attributes?.role;

      if (!['admin', 'superadmin'].includes(userRole)) return;

      const result = await tokenServices.getAll();
      const lists = [];

      result.data.forEach((item) => {
        const { description } = item?.attributes;

        const status = item?.attributes?.is_used ? 'Used' : 'Free';
        const createdBy = item?.relationships?.token_created_by?.username;
        const usedBy = item?.relationships?.token_used_by?.username;
        lists.push({ id: item.id, description, usedBy, createdBy, status });
      });

      setTokens(lists);
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    getTokens();
  }, [toggleRefresh, user]);

  const createToken = async () => {
    try {
      if (!description) return toast.error('Harap isi deskripsi');
      const body = {
        data: {
          attributes: {
            description,
            created_by: user?.attributes?.id
          }
        }
      };
      const result = await tokenServices.addNew(body);

      if (result?.data?.id) {
        toast.success('Formulir berhasil dibuat');
        setToggleRefresh(!toggleRefresh);
        setDescription('');
      } else {
        toast.error('Maaf, terjadi kesalahan');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusColor = (status) => {
    if (status.toLowerCase() === 'used') return 'warning';
    if (status.toLowerCase() === 'free') return 'secondary';

    return 'secondary';
  };

  return (
    <Page title="Token | Peladen Pemda DIY">
      {isEligible('page_token') ? (
        <Container>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Typography variant="h4" gutterBottom>
              Token
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Deskripsi"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
              />
              <Button
                variant="contained"
                onClick={() => createToken()}
                startIcon={<Icon icon={plusFill} />}
              >
                New Token
              </Button>
            </Stack>
          </Stack>

          <Stack direction="column" spacing={2} py={2}>
            <Typography variant="body2">
              Halaman Token ini digunakan sebagai validasi untuk memfasilitasi permintaan khusus
              seperti permintaan private VM/VPS dll.
            </Typography>
          </Stack>

          <Card>
            <TokenListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <TokenListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={tokens.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const { id, description, createdBy, usedBy, status, createdAt } = row;

                        return (
                          <TableRow hover key={id} tabIndex={-1}>
                            <TableCell align="left" />
                            <TableCell align="left">{id}</TableCell>
                            <TableCell align="left">{description}</TableCell>
                            <TableCell align="left">{createdBy}</TableCell>
                            <TableCell align="left">{usedBy}</TableCell>
                            <TableCell align="left">
                              <Label variant="ghost" color={getStatusColor(status)}>
                                {sentenceCase(status || 'undefined')}
                              </Label>
                            </TableCell>

                            <TableCell align="left">
                              {moment.tz(createdAt, 'Asia/Jakarta').format('YYYY-MM-DD')}
                            </TableCell>
                            <TableCell align="right">
                              <TokenMoreMenu onDelete={deleteData} rowData={{ id, status }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 20, 30]}
              component="div"
              count={tokens.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </Container>
      ) : (
        <AccessRequired />
      )}
    </Page>
  );
}
